import { BaseService } from './base.service';

/** Per-channel control for one Community notification type. */
export interface CommunityChannelPreference {
  push?: boolean;
  inApp?: boolean;
  email?: boolean;
}

/**
 * The Community sub-document on the member's notification settings.
 *
 * ⚠️ **The API replaces this object wholesale.** A PUT carrying a partial
 * `community` erases every key it omits, so callers must send the full object
 * they want stored — see `useUpdateCommunityNotifications`.
 *
 * An absent key means "never configured", which is not the same as off: the
 * server falls back to on for push, and to a per-type default for email.
 */
export interface CommunityNotificationPreferences {
  /** Community-scoped master switch. `false` silences all of Community. */
  enabled?: boolean;
  inviteReceived?: CommunityChannelPreference;
  inviteAccepted?: CommunityChannelPreference;
  connectionBooked?: CommunityChannelPreference;
  announcementPosted?: CommunityChannelPreference;
  reactionsOnMyItem?: CommunityChannelPreference;
  /** Kept while announcements are switched off — see the settings pane. */
  mentionedInFeed?: CommunityChannelPreference;
  /** Someone asked to join a tee time you shared. */
  teeTimeInterest?: CommunityChannelPreference;
  /** A host let you into their round. */
  teeTimeAccepted?: CommunityChannelPreference;
  quietHours?: { startMinute: number; endMinute: number; timezone: string };
  bundling?: { reactionsOnMyItem?: boolean; connectionBooked?: boolean };
}

/**
 * Per-channel push opt-ins. `appNotification` is the master switch — turning it
 * off silences every channel regardless of these.
 */
export interface NotificationChannels {
  promotions: boolean;
  announcements: boolean;
  accountAlerts: boolean;
}

export interface NotificationSettings {
  id: string;
  memberId: string;
  appNotification: boolean;
  emailNotification: boolean;
  community?: CommunityNotificationPreferences;
  channels: NotificationChannels;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateNotificationSettingsDto {
  appNotification?: boolean;
  emailNotification?: boolean;
  /** Replaced wholesale by the API — send the complete object. */
  community?: CommunityNotificationPreferences;
  /** Partial — omitted channels keep their current value. */
  channels?: Partial<NotificationChannels>;
}

class NotificationService extends BaseService {
  constructor() {
    super('/member-notifications');
  }

  /**
   * Get notification settings for authenticated member
   */
  async getNotificationSettings(): Promise<NotificationSettings> {
    return this.get<NotificationSettings>('');
  }

  /**
   * Update notification settings for authenticated member
   */
  async updateNotificationSettings(
    updateDto: UpdateNotificationSettingsDto
  ): Promise<NotificationSettings> {
    return this.put<NotificationSettings>('', updateDto);
  }

  /**
   * Delete notification settings for authenticated member
   */
  async deleteNotificationSettings(): Promise<void> {
    return this.delete<void>('');
  }
}

export const notificationService = new NotificationService();
export default notificationService;
